import { useEffect, useState } from 'react';
import { AdminApiClient } from './lib/api.js';
import type {
  AuditEvent,
  MerchantSummary,
  WebhookDeadLetter,
  WebhookDelivery,
} from './lib/api.js';
import { LoginCard } from './components/LoginCard.js';
import { MerchantCard } from './components/MerchantCard.js';
import { AuditTable } from './components/AuditTable.js';
import { WebhooksTable } from './components/WebhooksTable.js';
import { MasterWalletChangeForm } from './components/MasterWalletChangeForm.js';

type Tab = 'overview' | 'master-wallet' | 'audit' | 'webhooks';

const STORAGE_KEY = 'cryptopay.admin.apikey';

function readEnv(): { apiBaseUrl: string } {
  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:3001',
  };
}

export function App() {
  const env = readEnv();
  const [client, setClient] = useState<AdminApiClient | null>(null);
  const [merchant, setMerchant] = useState<MerchantSummary | null>(null);
  const [auditEvents, setAuditEvents] = useState<readonly AuditEvent[]>([]);
  const [deliveries, setDeliveries] = useState<readonly WebhookDelivery[]>([]);
  const [deadLetters, setDeadLetters] = useState<readonly WebhookDeadLetter[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cachedKey = typeof localStorage !== 'undefined'
      ? localStorage.getItem(STORAGE_KEY)
      : null;
    if (cachedKey) {
      void handleLogin(cachedKey, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogin(apiKey: string, persist = true): Promise<void> {
    const newClient = new AdminApiClient({ baseUrl: env.apiBaseUrl, apiKey });
    const m = await newClient.getMerchant();
    setClient(newClient);
    setMerchant(m);
    if (persist && typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, apiKey);
    }
    void loadAll(newClient);
  }

  function handleLogout(): void {
    setClient(null);
    setMerchant(null);
    setAuditEvents([]);
    setDeliveries([]);
    setDeadLetters([]);
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY);
  }

  async function loadAll(c: AdminApiClient): Promise<void> {
    setError(null);
    try {
      const [events, hooks] = await Promise.all([
        c.listAuditEvents(),
        c.listWebhooks(),
      ]);
      setAuditEvents(events);
      setDeliveries(hooks.deliveries);
      setDeadLetters(hooks.deadLetters);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }

  if (!client || !merchant) {
    return (
      <div className="admin-shell">
        <LoginCard onSubmit={handleLogin} />
      </div>
    );
  }

  return (
    <div className="admin-shell" role="main">
      <h1>Crypto-pay Admin</h1>
      {error ? <div className="error">{error}</div> : null}
      <MerchantCard merchant={merchant} onLogout={handleLogout} />
      <div className="tabs">
        <div className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          Genel
        </div>
        <div
          className={`tab ${tab === 'master-wallet' ? 'active' : ''}`}
          onClick={() => setTab('master-wallet')}
        >
          Master Wallet
        </div>
        <div className={`tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>
          Audit Log
        </div>
        <div
          className={`tab ${tab === 'webhooks' ? 'active' : ''}`}
          onClick={() => setTab('webhooks')}
        >
          Webhook'lar
        </div>
      </div>
      {tab === 'overview' ? (
        <div className="card">
          <h2 style={{ margin: '0 0 8px' }}>Özet</h2>
          <p className="muted" style={{ fontSize: 13 }}>
            Audit log: {auditEvents.length} kayıt · Webhook teslimatı:{' '}
            {deliveries.length} · DLQ: {deadLetters.length}
          </p>
          <button className="btn secondary" onClick={() => void loadAll(client)}>
            Yenile
          </button>
        </div>
      ) : null}
      {tab === 'master-wallet' ? (
        <MasterWalletChangeForm
          onPropose={async (wallets, proposedBy) => {
            const r = await client.proposeMasterWallet(wallets, proposedBy);
            await loadAll(client);
            return { changeId: r.change.changeId, confirmationToken: r.confirmationToken };
          }}
          onConfirm={async (changeId, token) => {
            await client.confirmMasterWallet(changeId, token);
            const m = await client.getMerchant();
            setMerchant(m);
            await loadAll(client);
          }}
          onCancel={async (changeId) => {
            await client.cancelMasterWallet(changeId);
            await loadAll(client);
          }}
        />
      ) : null}
      {tab === 'audit' ? (
        <AuditTable events={auditEvents} onRefresh={() => void loadAll(client)} />
      ) : null}
      {tab === 'webhooks' ? (
        <WebhooksTable
          deliveries={deliveries}
          deadLetters={deadLetters}
          onRefresh={() => void loadAll(client)}
        />
      ) : null}
    </div>
  );
}
